/**
 * Workshop Scheduler Utility
 * Automatically calculates and updates workshop timing
 */

// UPDATED: Workshop configuration - Sunday 10:00 AM to 1:00 PM IST (3 hours)
const WORKSHOP_CONFIG = {
  dayOfWeek: 0, // Sunday (0=Sunday, 1=Monday, ..., 6=Saturday)
  startTime: { hour: 10, minute: 0 }, // 10:00 AM
  endTime: { hour: 13, minute: 0 },   // 1:00 PM
  timezone: 'Asia/Kolkata',           // IST timezone
  duration: 3                         // 3 hours
};

/**
 * Get the next workshop date from current date
 * @returns {Date} Next workshop date
 */
export function getNextWorkshopDate() {
  const now = new Date();
  const currentDay = now.getDay();
  const currentHour = now.getHours();
  const currentMinute = now.getMinutes();

  // Calculate days until next Sunday
  let daysUntilWorkshop;

  if (currentDay === WORKSHOP_CONFIG.dayOfWeek) {
    // If today is Sunday, check if workshop time has passed
    const workshopTime = WORKSHOP_CONFIG.startTime.hour * 60 + WORKSHOP_CONFIG.startTime.minute;
    const currentTime = currentHour * 60 + currentMinute;

    if (currentTime < workshopTime) {
      // Workshop hasn't started yet today
      daysUntilWorkshop = 0;
    } else {
      // Workshop has passed, next one is in 7 days
      daysUntilWorkshop = 7;
    }
  } else {
    // Calculate days until next Sunday
    daysUntilWorkshop = (WORKSHOP_CONFIG.dayOfWeek - currentDay + 7) % 7;
    if (daysUntilWorkshop === 0) daysUntilWorkshop = 7;
  }

  // Create next workshop date
  const nextWorkshop = new Date(now);
  nextWorkshop.setDate(now.getDate() + daysUntilWorkshop);
  nextWorkshop.setHours(WORKSHOP_CONFIG.startTime.hour);
  nextWorkshop.setMinutes(WORKSHOP_CONFIG.startTime.minute);
  nextWorkshop.setSeconds(0);
  nextWorkshop.setMilliseconds(0);

  return nextWorkshop;
}

/**
 * Get day suffix (st, nd, rd, th)
 * @param {number} day - Day of month
 * @returns {string} Suffix
 */
function getDaySuffix(day) {
  if (day > 3 && day < 21) return 'th';
  switch (day % 10) {
    case 1: return 'st';
    case 2: return 'nd';
    case 3: return 'rd';
    default: return 'th';
  }
}

/**
 * Get formatted workshop date string
 * Format: "Sunday, December 22nd at 10:00 AM IST"
 * @returns {string} Formatted date string
 */
export function getFormattedWorkshopDate() {
  const nextWorkshop = getNextWorkshopDate();
  
  const dayNames = [
    'Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'
  ];
  
  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June', 
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  const dayName = dayNames[nextWorkshop.getDay()];
  const monthName = monthNames[nextWorkshop.getMonth()];
  const day = nextWorkshop.getDate();
  const daySuffix = getDaySuffix(day);

  // Format time (e.g., "10:00 AM")
  let hours = WORKSHOP_CONFIG.startTime.hour;
  const minutes = WORKSHOP_CONFIG.startTime.minute.toString().padStart(2, '0');
  const ampm = hours >= 12 ? 'PM' : 'AM';
  hours = hours % 12 || 12;

  return `${dayName}, ${monthName} ${day}${daySuffix} at ${hours}:${minutes} ${ampm} IST`;
}

/**
 * Get workshop time string
 * @returns {string} Workshop time string
 */
export function getWorkshopTimeString() {
  const startTime = `${WORKSHOP_CONFIG.startTime.hour === 12 ? 12 : WORKSHOP_CONFIG.startTime.hour > 12 ? WORKSHOP_CONFIG.startTime.hour - 12 : WORKSHOP_CONFIG.startTime.hour}:${WORKSHOP_CONFIG.startTime.minute.toString().padStart(2, '0')} ${WORKSHOP_CONFIG.startTime.hour >= 12 ? 'PM' : 'AM'}`;
  
  const endTime = `${WORKSHOP_CONFIG.endTime.hour === 12 ? 12 : WORKSHOP_CONFIG.endTime.hour > 12 ? WORKSHOP_CONFIG.endTime.hour - 12 : WORKSHOP_CONFIG.endTime.hour}:${WORKSHOP_CONFIG.endTime.minute.toString().padStart(2, '0')} ${WORKSHOP_CONFIG.endTime.hour >= 12 ? 'PM' : 'AM'}`;
  
  return `${startTime} - ${endTime} IST`;
}

/**
 * Get countdown target date (next workshop start time)
 * @returns {Date} Target date for countdown
 */
export function getCountdownTargetDate() {
  return getNextWorkshopDate();
}

/**
 * Check if it's time to update (Sunday 10:00 AM)
 * @returns {boolean} True if it's update time
 */
export function isUpdateTime() {
  const now = new Date();
  const day = now.getDay();
  const hour = now.getHours();
  const minute = now.getMinutes();
  
  return day === 0 && hour === 10 && minute === 0; // Sunday 10:00 AM
}

/**
 * Get workshop status text
 * @returns {string} Status text
 */
export function getWorkshopStatus() {
  const now = new Date();
  const nextWorkshop = getNextWorkshopDate();
  const timeDiff = nextWorkshop.getTime() - now.getTime();
  const daysLeft = Math.ceil(timeDiff / (1000 * 60 * 60 * 24));

  if (daysLeft === 0) {
    return 'Workshop is TODAY!';
  } else if (daysLeft === 1) {
    return 'Workshop is TOMORROW!';
  } else if (daysLeft <= 7) {
    return `Workshop in ${daysLeft} days`;
  } else {
    return 'Next workshop coming soon';
  }
}

/**
 * Get workshop details object
 * @returns {Object} Workshop details
 */
export function getWorkshopDetails() {
  const nextWorkshop = getNextWorkshopDate();
  
  return {
    date: nextWorkshop,
    formattedDate: getFormattedWorkshopDate(),
    timeString: getWorkshopTimeString(),
    status: getWorkshopStatus(),
    countdown: nextWorkshop.toISOString(),
    dayOfWeek: 'Sunday',
    duration: `${WORKSHOP_CONFIG.duration} hours`,
    timezone: 'IST'
  };
}